import { computed, ref, watch } from 'vue';
import { useUserStoreWithOut } from '/@/store/modules/user';
import {
  useMouse,
  useTimeoutFn,
  tryOnUnmounted,
  watchThrottled,
  useBroadcastChannel,
} from '@vueuse/core';

import { useMessage } from '/@/hooks/web/useMessage';
import { router } from '/@/router';
import { PageEnum } from '/@/enums/pageEnum';
import { useI18n } from '@mcex/locale';
import { RouteLocationRaw } from 'vue-router';
import { BROADCAST_REFRESH_PAGE_KEY, BROADCAST_TOKEN_KEY } from '/@/enums/cacheEnum';
import { Modal } from 'ant-design-vue';

let unWatchMouse: Nullable<() => void> = null;
function stopWatchMouse() {
  unWatchMouse?.();
  unWatchMouse = null;
}
let sinUpConstance: Nullable<ReturnType<typeof Modal.warning>> = null;
/**
 * 提示确认退出登录
 * @param isAccountDisabled 账号是否被禁用
 * @param closeCallback 关闭回调
 */
export function confirmSinUp(isAccountDisabled = false, closeCallback?: () => void) {
  if (sinUpConstance) {
    return;
  }
  const { t } = useI18n();
  const { createWarningModal } = useMessage();
  sinUpConstance = createWarningModal({
    title: t('sys.api.errorTip'),
    content: isAccountDisabled ? t('sys.exception.accountDisabled') : t('sys.api.timeoutMessage'),
    okText: t('sys.exception.logBackIn'),
    afterClose: () => {
      sinUpConstance = null;
      closeCallback ? closeCallback() : router.push(PageEnum.BASE_LOGIN);
    },
  });
}
/**
 * 登出
 * 账户未登录过则直接跳出
 * @param isAccountDisabled 账号是否被禁用
 * @param closeCallback 关闭回调 默认跳转登录页
 */
export function logoutFn(isAccountDisabled = false, closeCallback?: () => void) {
  const userStore = useUserStoreWithOut();
  if (!userStore.getToken) return;

  stopWatchMouse();
  userStore.logout(false);
  confirmSinUp(isAccountDisabled, closeCallback);
}
export const useRefreshToken = () => {
  const RFTIMEOUT = 1000 * 60 * 5;
  const LGTIMEOUT = 1000 * 60 * 30;
  const { x, y } = useMouse();
  const userStore = useUserStoreWithOut();

  function goLoginPage() {
    const currentRoute = router.currentRoute.value;
    let query: { redirect: string } | undefined = undefined;
    const toPackage = currentRoute.matched[0]?.meta?.package;
    if (toPackage) {
      query = {
        redirect: currentRoute?.fullPath,
      };
    }
    const redirect: RouteLocationRaw = {
      path: PageEnum.BASE_LOGIN,
      query,
    };
    router.push(redirect);
  }
  // 广播
  const { isSupported, post, data } = useBroadcastChannel({
    name: BROADCAST_TOKEN_KEY,
  });
  // 接受广播
  watch(data, () => {
    if (!data.value) {
      return;
    }
    data.value.token && userStore.setToken(data.value.token);
    data.value.refreshToken && userStore.setRefreshToken(data.value.refreshToken);
  });
  // 广播刷新页面
  const { data: freshPage } = useBroadcastChannel({
    name: BROADCAST_REFRESH_PAGE_KEY,
  });
  watch(freshPage, () => {
    if (!freshPage.value) {
      return;
    }
    const currentRoute = router.currentRoute.value;
    const toPackage =
      currentRoute.matched[0]?.meta?.package ||
      ((currentRoute.query?.redirect ?? '') as string).split('/')[1];
    if (freshPage.value.currentPackage === toPackage) {
      return;
    }
    if (!freshPage.value.fresh) {
      return;
    }
    window.location.reload();
  });

  const lastRefreshTokenTime = ref(new Date().getTime());
  const nextRefreshTokenTime = computed(() => {
    return new Date(lastRefreshTokenTime.value + RFTIMEOUT).getTime();
  });

  /**
   * 刷新token
   * 1、更新上一次刷新token的时间
   * 2、执行刷新token操作
   * 3、token或refreshtoken存在则刷新token
   * =>失败则重新登录
   */
  function refreshTokenFn() {
    lastRefreshTokenTime.value = new Date().getTime();
    userStore.getToken &&
      userStore
        .refreshTokenAction()
        .then((res) => {
          if (res && isSupported) {
            post({ token: userStore.getToken, refreshToken: userStore.getRefreshToken });
          }
        })
        .catch((error) => {
          // 重新登录
          const desc = error.response?.data?.error_description as string;
          const isAccountDisabled = desc === 'Account disabled';
          logoutFn(isAccountDisabled, goLoginPage);
        });
  }

  /**
   * 5分钟刷新token
   */
  const {
    isPending: isRefreshPending,
    start: refreshStart,
    stop: refreshStop,
  } = useTimeoutFn(() => refreshTokenFn(), RFTIMEOUT, { immediate: false });

  /**
   * 30分钟无操作则登出
   */
  const {
    isPending: isLogoutPending,
    start: logoutStart,
    stop: logoutStop,
  } = useTimeoutFn(() => logoutFn(false, goLoginPage), LGTIMEOUT, { immediate: false });

  /**
   * 监听鼠标移动
   */
  const startWatchMouse = () => {
    if (unWatchMouse) return;
    unWatchMouse = watchThrottled(
      () => [x.value, y.value],
      () => {
        // 鼠标移动时停止登出计时
        logoutStop();
        // 已经到达下一次刷新时间，立即刷新token
        if (nextRefreshTokenTime.value < new Date().getTime()) {
          refreshStop();
          refreshTokenFn();
        }
        // 刷新token定时器不存在时，重新开启刷新定时器
        if (!isRefreshPending.value) {
          refreshStart();
        }
        // 退出定时器不存在时，重新开启退出定时器
        !isLogoutPending.value && logoutStart();
      },
      {
        throttle: 1000,
      },
    );
  };

  /**
   * token变化时，开启登出计时
   */
  watch(
    () => userStore.getToken,
    (val) => {
      if (val) {
        startWatchMouse();
        logoutStart();
        tryOnUnmounted(() => {
          refreshStop();
          logoutStop();
        });
      } else {
        // 如果已登出，停止刷新token和登出计时
        refreshStop();
        logoutStop();
      }
    },
    {
      immediate: true,
    },
  );
};
